'use client';
import { useCallback } from 'react';

import { useAtom, useAtomValue } from 'jotai';
import Link from 'next/link';

import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { postcodeAtom } from '@core/Atoms/Postcode/Postcode.atom';
import { locationAtom } from '@core/Atoms/Search/LocationAtom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import type { Locale } from '@core/Entities/Locale/Locale.entity';
import Button from '@gds/Button/Button';
import { RichText } from '@page-builder/Components/RichText/RichText';

import { enrichPlpPath, trackClick } from '../Utils/CampaignPromo.utils';

import styles from './CampaignBlock.module.css';

interface CampaignBlockProps {
  cta: string;
  colourScheme: any;
  imageUrl: string;
  isActive: boolean;
  locale: Locale;
  text: string;
  url: string;
  blockLoc?: string;
}

export const CampaignBlock = ({
  blockLoc,
  colourScheme,
  cta,
  imageUrl,
  isActive,
  locale,
  text,
  url,
}: CampaignBlockProps) => {
  const color = colourScheme[0].codename;

  const pcAtom = useAtom(postcodeAtom);
  const location = useAtomValue(locationAtom);
  const userId = useAtomValue(userIdAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const formState = useAtomValue(formStateAtom);

  const trimUrl = url.trim();
  const plpPath = enrichPlpPath(trimUrl, pcAtom, location, locale);

  const handleClick = useCallback(() => {
    trackClick(
      {
        type: 'block',
        pathWithLocale: plpPath.split('?')[0],
        text,
        location: blockLoc,
      },
      {
        searchParams,
        userId,
        formState,
      },
    );

    return;
  }, [url, pcAtom[0], pcAtom[1], locale, location]);

  if (!isActive) return null;
  return (
    <div
      className={styles.wrpr}
      data-color={color}
      onClick={handleClick}
      style={{ backgroundImage: `url(${imageUrl})` }}
    >
      <RichText richText={text} />
      <Button Component={Link} href={plpPath} variant="outlined" onClick={handleClick}>
        {cta}
      </Button>
    </div>
  );
};

CampaignBlock.displayName = 'CampaignBlock';
